import type { PlayerEconomyState } from '../economy/types';

export type ClaudiStickerKind = 'positive' | 'negative';

export type ClaudiStickerId =
  | 'claudi_first_success'
  | 'claudi_sweet_ride'
  | 'claudi_nimble'
  | 'claudi_boost_perfect'
  | 'claudi_tidy'
  | 'claudi_second_chance'
  | 'claudi_all_heroes'
  | 'claudi_almost_late'
  | 'claudi_sweet_diet'
  | 'claudi_heartbreaker'
  | 'claudi_bumpy'
  | 'claudi_worse_replay'
  | 'claudi_no_boost';

/** Старые id сохранений → актуальные id из GDD. */
export const LEGACY_CLAUDI_STICKER_IDS: Partial<Record<string, ClaudiStickerId>> = {
  claudi_first_delivery: 'claudi_first_success',
  claudi_perfect_two: 'claudi_tidy',
};

export type ClaudiStickerDef = {
  id: ClaudiStickerId;
  name: string;
  emoji: string;
  hint: string;
  kind: ClaudiStickerKind;
};

/** Полный каталог стикеров Клауди (GDD v2: сначала положительные, затем негативные). */
export const CLAUDI_STICKERS: readonly ClaudiStickerDef[] = [
  {
    id: 'claudi_first_success',
    name: 'Доставлено улётно: первый раз и сразу успех',
    emoji: '🚀',
    hint: 'Первая посылка, доставленная успешно',
    kind: 'positive',
  },
  {
    id: 'claudi_sweet_ride',
    name: 'Сладкий рейс: собрал все зефирки',
    emoji: '🍬',
    hint: 'Собрал все зефирки на уровне',
    kind: 'positive',
  },
  {
    id: 'claudi_nimble',
    name: 'Ловкий малый: ни одного столкновения',
    emoji: '🎯',
    hint: 'Прошел уровень без касания препятствий',
    kind: 'positive',
  },
  {
    id: 'claudi_boost_perfect',
    name: 'Улётный разгон: бустер сработал идеально',
    emoji: '⚡',
    hint: 'Использовал бустер без столкновений',
    kind: 'positive',
  },
  {
    id: 'claudi_tidy',
    name: 'Порядочек есть: Клауди одобряет',
    emoji: '⭐',
    hint: 'Выполнить доставку выше 4,8 баллов',
    kind: 'positive',
  },
  {
    id: 'claudi_second_chance',
    name: 'Второй шанс: улучшил прошлый результат',
    emoji: '🔄',
    hint: 'Улучшить результат после повторного прохождения',
    kind: 'positive',
  },
  {
    id: 'claudi_all_heroes',
    name: 'Опытный наездник: выполнил доставки всеми героями',
    emoji: '🦸',
    hint: 'Выполнил доставки всеми героями',
    kind: 'positive',
  },
  {
    id: 'claudi_almost_late',
    name: 'Почти успел: финишировал на последних секундах',
    emoji: '⏱️',
    hint: 'Успел до завершения лимита по времени',
    kind: 'negative',
  },
  {
    id: 'claudi_sweet_diet',
    name: 'Сладкая диета: собрал меньше половины зефирок',
    emoji: '🍭',
    hint: 'Собрать меньше половины зефирок',
    kind: 'negative',
  },
  {
    id: 'claudi_heartbreaker',
    name: 'Сердцеед: потратил все жизни',
    emoji: '💔',
    hint: 'Потратил все жизни, но прошел',
    kind: 'negative',
  },
  {
    id: 'claudi_bumpy',
    name: 'Посылка удивилась: было много встрясок',
    emoji: '📦',
    hint: 'Попал в половину препятствий',
    kind: 'negative',
  },
  {
    id: 'claudi_worse_replay',
    name: 'Клауди записала: повторил доставку, а стало хуже',
    emoji: '📝',
    hint: 'Повторил заказ, но ухудшил рейтинг доставки',
    kind: 'negative',
  },
  {
    id: 'claudi_no_boost',
    name: 'На честном облачке: без бустеров',
    emoji: '☁️',
    hint: 'Прошел уровень без бустеров',
    kind: 'negative',
  },
];

const TIDY_RATING_THRESHOLD = 4.8;

export type StickerUnlockContext = {
  outcome: 'completed' | 'failed' | 'aborted';
  rating: number;
  sparksCollected: number;
  sparksTotal: number;
  isFirstSuccess: boolean;
  replayImproved: boolean;
  replayWorsened: boolean;
  collisionFree?: boolean;
  boosterUsedWithoutCollision?: boolean;
  finishedNearTimeLimit?: boolean;
  usedAllLives?: boolean;
  obstacleHitRatio?: number;
  noBoostersUsed?: boolean;
};

function normalizeStickerId(id: string): ClaudiStickerId | null {
  const mapped = LEGACY_CLAUDI_STICKER_IDS[id] ?? id;
  return CLAUDI_STICKERS.some((s) => s.id === mapped) ? (mapped as ClaudiStickerId) : null;
}

function hasSticker(state: PlayerEconomyState, id: ClaudiStickerId): boolean {
  return (state.stickers ?? []).some((s) => normalizeStickerId(s.id) === id);
}

function canUnlock(
  id: ClaudiStickerId,
  _state: PlayerEconomyState,
  ctx: StickerUnlockContext,
): boolean {
  if (ctx.outcome !== 'completed') {
    return false;
  }
  switch (id) {
    case 'claudi_first_success':
      return ctx.isFirstSuccess;
    case 'claudi_sweet_ride':
      return ctx.sparksTotal > 0 && ctx.sparksCollected >= ctx.sparksTotal;
    case 'claudi_nimble':
      return ctx.collisionFree === true;
    case 'claudi_boost_perfect':
      return ctx.boosterUsedWithoutCollision === true;
    case 'claudi_tidy':
      return ctx.rating >= TIDY_RATING_THRESHOLD;
    case 'claudi_second_chance':
      return ctx.replayImproved;
    case 'claudi_all_heroes':
      return false;
    case 'claudi_almost_late':
      return ctx.finishedNearTimeLimit === true;
    case 'claudi_sweet_diet':
      return ctx.sparksTotal > 0 && ctx.sparksCollected < ctx.sparksTotal * 0.5;
    case 'claudi_heartbreaker':
      return ctx.usedAllLives === true;
    case 'claudi_bumpy':
      return ctx.obstacleHitRatio != null && ctx.obstacleHitRatio >= 0.5;
    case 'claudi_worse_replay':
      return ctx.replayWorsened;
    case 'claudi_no_boost':
      return ctx.noBoostersUsed === true;
    default:
      return false;
  }
}

export function unlockClaudiStickers(
  state: PlayerEconomyState,
  ctx: StickerUnlockContext,
  now: number,
): { state: PlayerEconomyState; unlockedIds: ClaudiStickerId[] } {
  const unlockedIds: ClaudiStickerId[] = [];
  let stickers = [...(state.stickers ?? [])];
  for (const def of CLAUDI_STICKERS) {
    if (hasSticker({ ...state, stickers }, def.id)) {
      continue;
    }
    if (!canUnlock(def.id, { ...state, stickers }, ctx)) {
      continue;
    }
    stickers.push({ id: def.id, unlockedAt: now });
    unlockedIds.push(def.id);
  }
  return { state: { ...state, stickers }, unlockedIds };
}

export function isClaudiStickerUnlocked(state: PlayerEconomyState, id: ClaudiStickerId): boolean {
  return hasSticker(state, id);
}
